// Live Performance Tracking — browser data layer.
//
// Same conventions as signalDataFeeds.js: shared API base resolution, bearer token from
// localStorage, errors carrying a machine-readable `code`.

const LOCAL_HOST_PATTERN = /^(localhost|127\.0\.0\.1)$/i;
const API_BASE_URL = (() => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && LOCAL_HOST_PATTERN.test(window.location.hostname)) {
    return 'http://localhost:5000';
  }
  return '';
})();

async function requestLive(path, options = {}) {
  const { method = 'GET', body } = options;
  const headers = {};
  if (body) headers['Content-Type'] = 'application/json';
  const token = typeof window !== 'undefined' ? localStorage.getItem('userToken') : null;
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: Object.keys(headers).length ? headers : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload.error || 'Live tracking request failed.');
    error.status = response.status;
    if (payload.code) error.code = payload.code;
    throw error;
  }
  return payload;
}

export function listLiveLinks() {
  return requestLive('/api/live/links');
}

export function createLiveLink({ label, strategyId } = {}) {
  return requestLive('/api/live/links', { method: 'POST', body: { label, strategyId } });
}

export function revokeLiveLink(id) {
  return requestLive(`/api/live/links/${id}`, { method: 'DELETE' });
}

export function listLiveAccounts() {
  return requestLive('/api/live/accounts');
}

export function getLiveAccount(id) {
  return requestLive(`/api/live/accounts/${id}`);
}

export function updateLiveAccount(id, patch) {
  return requestLive(`/api/live/accounts/${id}`, { method: 'PATCH', body: patch });
}

// The one URL a user must whitelist inside MetaTrader. Derived from the configured API base so
// a local build tells the developer the truth rather than the production hostname.
export const WEBREQUEST_ORIGIN = (() => {
  if (API_BASE_URL) {
    try { return new URL(API_BASE_URL).origin; } catch (error) { /* fall through */ }
  }
  if (typeof window !== 'undefined') return window.location.origin;
  return 'https://vixalgotrading.com';
})();

export const CONNECTION_LABELS = {
  live: 'Reporting',
  idle: 'Quiet',
  offline: 'Offline',
  never: 'Not connected',
};

export const CONSISTENCY_LABELS = {
  consistent: 'Matches the cloud run',
  partial: 'Partly matches',
  divergent: 'Differs from the cloud run',
};

export function formatMoney(value, currency = 'USD') {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  return `${number < 0 ? '-' : ''}${currency === 'USD' ? '$' : ''}${Math.abs(number).toFixed(2)}${currency === 'USD' ? '' : ` ${currency}`}`;
}

export function formatSigned(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  return `${number >= 0 ? '+' : ''}${number.toFixed(2)}`;
}

/**
 * Rebase an equity series to percentage return from its own first point.
 *
 * Required for the three-way overlay: the cloud account, the backtest and the real MT5 account
 * all start from different deposits, so plotting raw balances would make them incomparable and
 * the largest account would simply dominate the chart. Percent-from-start is the only honest
 * way to put them on one axis.
 */
export function toPercentSeries(points, valueKey = 'equity', timeKey = 'time') {
  const clean = (points || [])
    .map((point) => ({ t: Number(point[timeKey] ?? point.barTime), v: Number(point[valueKey]) }))
    .filter((point) => Number.isFinite(point.t) && Number.isFinite(point.v));
  if (clean.length < 2) return [];
  const base = clean[0].v;
  if (!(Math.abs(base) > 0)) return [];
  return clean.map((point) => ({ t: point.t, v: ((point.v - base) / Math.abs(base)) * 100 }));
}
